import { useEffect, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { Alert, Button } from '../../components/ui'
import { correctPost, importPost } from '../../api/catalogue'
import type { CataloguePost, Correction, ParsedPost } from '../../api/catalogue'
import { t } from '../../i18n'

/**
 * What we hold about one advert, as a form rather than as a paragraph.
 *
 * The model reads first and is often right; it is also often short, because half
 * this channel's older adverts are an address and a price. Reading it back
 * read-only made the queue a dead end: a post the model could not place showed
 * "не указано" and there was no way to say it yourself, so the object could
 * never be created at all.
 *
 * So every field the import needs is here and every one is editable. Blank means
 * the advert did not say it — never zero, and never "no" (invariant 2), which is
 * why the deposit has a separate "без депозита" and the bills have three states
 * rather than a checkbox.
 */
export function PostForm({ post }: { readonly post: CataloguePost }) {
  const client = useQueryClient()
  const [draft, setDraft] = useState<Draft>(() => draftOf(post.parsed))
  const [saved, setSaved] = useState(false)
  // Whether this form holds anything the person typed. The sync refetches the
  // list every round, and a reading arriving mid-sentence must not replace what
  // somebody is in the middle of writing -- which is exactly what happens when a
  // form resets on every prop change.
  const edited = useRef(false)

  useEffect(() => {
    if (edited.current) return
    setDraft(draftOf(post.parsed))
  }, [post.parsed])

  function change(next: Draft) {
    edited.current = true
    setDraft(next)
    setSaved(false)
  }

  const save = useMutation({
    mutationFn: () => correctPost(post.id, correctionOf(draft)),
    onSuccess: () => {
      setSaved(true)
      // Saved, so the server's copy is now the person's own and may show again.
      edited.current = false
      void client.invalidateQueries({ queryKey: ['catalogue'] })
    },
  })

  // Creating the object saves first. The button used to sit in the card's
  // footer, away from the fields, and imported whatever was last stored -- so
  // typing a correction and pressing «Создать объект» built the object from the
  // model's reading and threw the typing away without a word.
  const create = useMutation({
    mutationFn: async () => {
      if (edited.current) await correctPost(post.id, correctionOf(draft))
      return importPost(post.id)
    },
    onSuccess: () => {
      edited.current = false
      void client.invalidateQueries({ queryKey: ['catalogue'] })
    },
  })

  const settled = post.state === 'imported'
  const busy = save.isPending || create.isPending

  return (
    <div className="post-form">
      <div className="post-form__grid">
        <Field label={t('catalogue.fields.kind')}>
          <select
            className="field__input"
            value={draft.kind}
            disabled={settled}
            onChange={(event) => {
              change({ ...draft, kind: event.target.value })
            }}
          >
            <option value="">{t('catalogue.values.pickKind')}</option>
            <option value="apartment">{t('catalogue.values.apartment')}</option>
            <option value="room">{t('catalogue.values.room')}</option>
            <option value="bed">{t('catalogue.values.bed')}</option>
          </select>
        </Field>

        <Text label={t('catalogue.fields.area')} value={draft.area} disabled={settled}
          onChange={(area) => { change({ ...draft, area }) }} />
        <Text label={t('catalogue.fields.address')} value={draft.address} disabled={settled}
          onChange={(address) => { change({ ...draft, address }) }} />

        <Text label={t('catalogue.fields.rent')} value={draft.rent} disabled={settled} numeric
          onChange={(rent) => { change({ ...draft, rent }) }} />

        <Field label={t('catalogue.fields.bills')}>
          <select
            className="field__input"
            value={draft.bills}
            disabled={settled}
            onChange={(event) => {
              change({ ...draft, bills: event.target.value })
            }}
          >
            {/* Three states, not a checkbox: "не сказано" and "сверх цены" lead
                to different replies, and only one of them is a fact about the flat. */}
            <option value="">{t('catalogue.values.billsUnknown')}</option>
            <option value="included">{t('catalogue.values.billsIncluded')}</option>
            <option value="ontop">{t('catalogue.values.billsOnTop')}</option>
          </select>
        </Field>

        <Text label={t('catalogue.fields.depositAmount')} value={draft.depositAmount} disabled={settled} numeric
          onChange={(depositAmount) => { change({ ...draft, depositAmount }) }} />
        <Text label={t('catalogue.fields.depositMonths')} value={draft.depositMonths} disabled={settled} numeric
          onChange={(depositMonths) => { change({ ...draft, depositMonths }) }} />

        <Field label={t('catalogue.fields.depositNone')}>
          <label className="post-form__check">
            <input
              type="checkbox"
              checked={draft.depositNone}
              disabled={settled}
              onChange={(event) => {
                change({ ...draft, depositNone: event.target.checked })
              }}
            />
            {t('catalogue.values.depositNone')}
          </label>
        </Field>

        <Text label={t('catalogue.fields.availableFrom')} value={draft.availableFrom} disabled={settled}
          placeholder="2026-10-01"
          onChange={(availableFrom) => { change({ ...draft, availableFrom }) }} />
        <Text label={t('catalogue.fields.availableText')} value={draft.availableText} disabled={settled}
          onChange={(availableText) => { change({ ...draft, availableText }) }} />

        <Text label={t('catalogue.fields.capacity')} value={draft.capacity} disabled={settled} numeric
          onChange={(capacity) => { change({ ...draft, capacity }) }} />
        <Text label={t('catalogue.fields.rooms')} value={draft.rooms} disabled={settled} numeric
          onChange={(rooms) => { change({ ...draft, rooms }) }} />
        <Text label={t('catalogue.fields.areaSqm')} value={draft.areaSqm} disabled={settled} numeric
          onChange={(areaSqm) => { change({ ...draft, areaSqm }) }} />
        <Text label={t('catalogue.fields.tenants')} value={draft.tenants} disabled={settled}
          onChange={(tenants) => { change({ ...draft, tenants }) }} />
      </div>

      {post.parsed && post.parsed.unclear.length > 0 ? (
        <div className="catalogue__unclear">
          <strong>{t('catalogue.unclear')}:</strong> {post.parsed.unclear.join('; ')}
        </div>
      ) : null}

      {settled ? null : (
        <div className="post-form__actions">
          <Button
            busy={create.isPending}
            disabled={busy}
            onClick={() => {
              create.mutate()
            }}
          >
            {t('catalogue.create')}
          </Button>
          <Button
            variant="secondary"
            busy={save.isPending}
            disabled={busy}
            onClick={() => {
              save.mutate()
            }}
          >
            {t('catalogue.saveFields')}
          </Button>
          {saved && !busy ? (
            <span className="post-form__saved">{t('catalogue.savedFields')}</span>
          ) : null}
        </div>
      )}

      {save.isError ? <Alert tone="error">{t('catalogue.saveFailed')}</Alert> : null}
      {create.isError ? (
        <Alert tone="error">
          {t('catalogue.importFailed', {
            reason: create.error instanceof Error ? create.error.message : '',
          })}
        </Alert>
      ) : null}
    </div>
  )
}

function Field({ label, children }: { readonly label: string; readonly children: React.ReactNode }) {
  return (
    <label className="post-form__field">
      <span className="post-form__label">{label}</span>
      {children}
    </label>
  )
}

function Text({
  label,
  value,
  onChange,
  disabled,
  numeric,
  placeholder,
}: {
  readonly label: string
  readonly value: string
  readonly onChange: (value: string) => void
  readonly disabled: boolean
  readonly numeric?: boolean
  readonly placeholder?: string
}) {
  return (
    <Field label={label}>
      <input
        className="field__input"
        value={value}
        disabled={disabled}
        inputMode={numeric === true ? 'decimal' : undefined}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value)
        }}
      />
    </Field>
  )
}

/** The form's own state: strings, because an empty input is "not said" and a
 * number type would turn that into a zero. */
interface Draft {
  kind: string
  area: string
  address: string
  rent: string
  bills: string
  depositAmount: string
  depositMonths: string
  depositNone: boolean
  availableFrom: string
  availableText: string
  capacity: string
  rooms: string
  areaSqm: string
  tenants: string
  tenantGender: string
}

function draftOf(parsed: ParsedPost | undefined): Draft {
  return {
    kind: parsed?.kind ?? '',
    area: parsed?.area ?? '',
    address: parsed?.addressExact ?? '',
    rent: money(parsed?.rentMinor),
    bills: parsed?.billsIncluded === undefined ? '' : parsed.billsIncluded ? 'included' : 'ontop',
    depositAmount: money(parsed?.depositMinor),
    depositMonths: parsed?.depositMonths === undefined ? '' : String(parsed.depositMonths),
    depositNone: parsed?.depositNone === true,
    availableFrom: day(parsed?.availableFrom),
    availableText: parsed?.availableFromText ?? '',
    capacity: parsed?.capacity === undefined ? '' : String(parsed.capacity),
    rooms: parsed?.rooms === undefined ? '' : String(parsed.rooms),
    areaSqm: parsed?.areaSqm === undefined ? '' : String(parsed.areaSqm),
    tenants: parsed?.tenants ?? '',
    tenantGender: parsed?.tenantGender ?? '',
  }
}

/** The day out of whatever the server sent.
 *
 * It arrives as RFC3339 -- "2026-09-01T00:00:00Z" -- and the field asks for a
 * day. Showing the timestamp was bad enough; saving it back was worse, because
 * the server parses "2006-01-02" strictly, failed, and cleared the date the
 * model had read correctly. Editing one field silently deleted another.
 */
function day(iso: string | undefined): string {
  if (iso === undefined) return ''
  const match = /^(\d{4}-\d{2}-\d{2})/.exec(iso)
  return match?.[1] ?? iso
}

/** Minor units to a figure somebody can read and edit. */
function money(minor: number | undefined): string {
  return minor === undefined ? '' : String(minor / 100)
}

function correctionOf(draft: Draft): Correction {
  return {
    kind: draft.kind,
    area: draft.area,
    address_exact: draft.address,
    rent: number(draft.rent),
    // Null clears it back to "не сказано", which a checkbox could not express.
    bills_included: draft.bills === '' ? null : draft.bills === 'included',
    deposit_amount: number(draft.depositAmount),
    deposit_months: number(draft.depositMonths),
    deposit_none: draft.depositNone,
    available_from: draft.availableFrom,
    available_from_text: draft.availableText,
    capacity: number(draft.capacity),
    rooms: number(draft.rooms),
    area_sqm: number(draft.areaSqm),
    tenants: draft.tenants,
    tenant_gender: draft.tenantGender,
  }
}

/** An empty field is zero on the wire, which the server reads as "not said". A
 * price of nothing is not a price.
 *
 * People write "1 400", "1400 €", "1.400" and paste figures with a non-breaking
 * space in them. Number() reads every one of those as NaN, which became 0, which
 * the server read as "not said" -- so typing the price the way it is written
 * erased it.
 */
function number(value: string): number {
  const cleaned = value
    .replace(/[\s\u00a0\u202f]/g, '')
    .replace(/[€$₽]/g, '')
    .replace(',', '.')
  if (cleaned === '') return 0

  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}
